import { scaleSymlog as d3ScaleSymlog, scaleLog, ScaleSymLog } from 'd3-scale';

import { defaultTickFormatter } from '../formatters/defaultTickFormatter';

/**
 * A wrapper around d3's `scaleSymlog` with enhancements around tick generation and `nice` domain
 * modifications.
 */
export const scaleSymlog = (locale?: string): ScaleSymLog<number, number> => {
  const initScale = (baseScale: ScaleSymLog<number, number>) => {
    const scale = baseScale;
    const originalCopy = baseScale.copy.bind(baseScale);

    const getLogScale = () => {
      const domain = scale.domain();

      // Log scales don't support including 0 in the domain. (Here, we assume the scale is
      // positive.) Replace domain min with the symlog constant to avoid including zero and to avoid
      // crunching tick values between (0, constant).
      const adjustedDomain = [Math.max(domain[0], scale.constant()), domain[1]];

      return scaleLog().domain(adjustedDomain).range(scale.range());
    };

    scale.copy = () => {
      return initScale(originalCopy());
    };

    scale.nice = () => {
      const logNiceDomain = getLogScale().nice().domain();

      // FIXME: it is not necessarily true that 0 will be the nicest minimum value, but log scale
      // can't handle 0 in the domain, so we assume that it is for convenience. A more sophisticated
      // approach would take into account the original domain and select a suitable "nice" minimum
      // (or just implement `nice` from scratch).
      scale.domain([0, logNiceDomain[1]]);
      return scale;
    };

    scale.ticks = (tickCount) => {
      // TODO: we may or may not want to include 0, which may or may not be included in the domain.
      // (The log scale will never include 0 because it's not supported.) And depending on the
      // choice of constant (usually 1) and available space, a 0 tick and the constant tick might be
      // too close together, which would have to be accounted for somewhere.
      return getLogScale().ticks(tickCount);
    };

    scale.tickFormat = (count, specifier) => {
      const logFormatter = getLogScale().tickFormat(count, specifier);
      return (d) => {
        // The log scale formatter sometimes returns empty strings because its tick generator can
        // returns many more ticks than requested (in order to better reflect the relationships
        // between values). We use it to determine whether or not to show anything for a particular
        // tick value, but otherwise use our own formatting.
        if (d !== 0 && logFormatter(d) === '') {
          return '';
        }
        return defaultTickFormatter(d, locale);
      };
    };

    return scale;
  };

  return initScale(d3ScaleSymlog());
};
